import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LmoFracsService } from '~lmo/services';
import { BehaviorSubject, Observable } from 'rxjs';
import { BoxPickupSite, CreateBoxPickupSiteRequest, FracToCopy } from '~lmo/models/frac.model';
import { LightTheme } from '../../../map/mapStyles';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

const metersInAMile = 1609;

@Component({
  selector: 'sa-create-box-pickup-sites-dialog',
  templateUrl: './create-box-pickup-sites-dialog.component.html',
  styleUrls: ['./create-box-pickup-sites-dialog.component.scss'],
})
export class CreateBoxPickupSitesDialogComponent implements OnInit {
  public createBoxPickUpSiteFormGroup: FormGroup;
  public siteNameFormControl: FormControl;
  public latitudeFormControl: FormControl;
  public longitudeFormControl: FormControl;
  public radiusInMilesFormControl: FormControl;
  public directionsFormControl: FormControl;
  public outOfRouteMilesFormControl: FormControl;
  public costPerMileFormControl: FormControl;
  public fracs$: Observable<FracToCopy[]>;
  public fracs: FracToCopy[];

  map: google.maps.Map;
  mapConfig: google.maps.MapOptions;
  geofence: google.maps.Circle;
  fracMarker: google.maps.Marker;
  mapBoundsChanged: BehaviorSubject<null> = new BehaviorSubject<null>(null);

  private editData: BehaviorSubject<BoxPickupSite> = new BehaviorSubject<BoxPickupSite>(null);

  public selectedFrac$ = new BehaviorSubject<FracToCopy>(null);
  filterFrac: FormControl = new FormControl();

  constructor(
    private fb: FormBuilder,
    private fracService: LmoFracsService,
    public snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<CreateBoxPickupSitesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  public ngOnInit(): void {
    this.fracService.fracsOptionsToCopy$.subscribe((items) => {
      this.fracs = items;
      this.fracs$ = this.filterFrac.valueChanges.pipe(
        startWith<string | any>(''),
        map((name) => (name ? this._filterFrac(name) : this.fracs)),
      );
    });

    if (this.data) {
      this.editData.next({
        id: this.data.id,
        name: this.data.name,
        siteId: this.data.siteId,
        outOfRouteMiles: this.data.outOfRouteMiles,
        outOfRouteRateCents: this.data.outOfRouteRateCents,
        lngLat: this.data.lngLat,
        radius: this.data.radius,
        createdAt: this.data.createdAt,
        directions: this.data.directions,
        hasActiveOrders: this.data.hasActiveOrders,
      });
    }

    this.initializeCreateBoxPickUpSiteForm();
  }

  private initializeCreateBoxPickUpSiteForm() {
    const existingSite = this.editData.value;
    this.createBoxPickUpSiteFormGroup = this.fb.group({
      frac: new FormControl(null),
      siteName: new FormControl(existingSite?.name ? existingSite.name : null, Validators.required),
      latitude: new FormControl(existingSite?.lngLat ? existingSite.lngLat[1] : 39.7392, Validators.required),
      longitude: new FormControl(existingSite?.lngLat ? existingSite.lngLat[0] : -104.9903, Validators.required),
      radiusInMiles: new FormControl(existingSite?.radius ? existingSite.radius / metersInAMile : null, [
        Validators.required,
        Validators.max(10),
        Validators.min(0.01),
      ]),
      directions: new FormControl(existingSite?.directions ? existingSite.directions : null),
      oorMiles: new FormControl(
        existingSite?.outOfRouteMiles ? existingSite.outOfRouteMiles : null,
        Validators.required,
      ),
      costPerMile: new FormControl(
        existingSite?.outOfRouteRateCents ? existingSite.outOfRouteRateCents / 100 : null,
        Validators.required,
      ),
    });

    this.siteNameFormControl = this.createBoxPickUpSiteFormGroup.controls['siteName'] as FormControl;
    this.latitudeFormControl = this.createBoxPickUpSiteFormGroup.controls['latitude'] as FormControl;
    this.longitudeFormControl = this.createBoxPickUpSiteFormGroup.controls['longitude'] as FormControl;
    this.radiusInMilesFormControl = this.createBoxPickUpSiteFormGroup.controls['radiusInMiles'] as FormControl;
    this.directionsFormControl = this.createBoxPickUpSiteFormGroup.controls['directions'] as FormControl;
    this.outOfRouteMilesFormControl = this.createBoxPickUpSiteFormGroup.controls['oorMiles'] as FormControl;
    this.costPerMileFormControl = this.createBoxPickUpSiteFormGroup.controls['costPerMile'] as FormControl;

    this.onFracChanges();
    this.onRadiusChanges();

    if (existingSite?.hasActiveOrders) {
      this.siteNameFormControl.disable();
      this.latitudeFormControl.disable();
      this.longitudeFormControl.disable();
      this.radiusInMilesFormControl.disable();
      this.directionsFormControl.disable();
    }

    this.initMap();
  }

  initMap() {
    const boxPickupSiteLocationValue = {
      latitude: this.latitudeFormControl.value ? this.latitudeFormControl.value : 39.7392,
      longitude: this.longitudeFormControl.value ? this.longitudeFormControl.value : -104.9903,
    };

    setTimeout(() => {
      const center = { lat: boxPickupSiteLocationValue.latitude, lng: boxPickupSiteLocationValue.longitude };
      this.mapConfig = {
        center: center,
        zoom: 12,
        streetViewControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          position: 3,
        },
        zoomControlOptions: {
          position: 3,
        },
        mapTypeId: google.maps.MapTypeId.HYBRID,
        styles: <any>LightTheme,
        draggable: !this.editData.value?.hasActiveOrders,
      };

      const element = document.getElementById('map-box-pickup-location');
      if (element != null) {
        this.map = new google.maps.Map(element, this.mapConfig);
        this.fracMarker = new google.maps.Marker({
          map: this.map,
          draggable: !this.editData.value?.hasActiveOrders,
          animation: google.maps.Animation.DROP,
          position: this.map.getCenter(),
        });
        this.geofence = new google.maps.Circle({
          map: this.map,
          radius: this.radiusInMilesFormControl.value * metersInAMile,
          strokeColor: '#00FF00',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#00FF00',
          fillOpacity: 0.35,
          editable: !this.editData.value?.hasActiveOrders,
          center: this.map.getCenter(),
          zIndex: 10,
        });
        this.geofence.bindTo('center', this.fracMarker, 'position');
        google.maps.event.addListener(this.fracMarker, 'position_changed', this.handleFracMarkerMove.bind(this));
        google.maps.event.addListener(this.geofence, 'radius_changed', this.handleGeofenceRadiusChange.bind(this));
        google.maps.event.addListener(this.map, 'bounds_changed', () => this.mapBoundsChanged.next(null));
      }
    }, 500);
  }

  private onFracChanges() {
    this.createBoxPickUpSiteFormGroup
      .get('frac')
      .valueChanges.pipe(debounceTime(400))
      .subscribe(() => {
        this.selectedFrac$.next(this.createBoxPickUpSiteFormGroup.get('frac').value as FracToCopy);

        if (this.createBoxPickUpSiteFormGroup.get('frac').value) {
          this.siteNameFormControl.setValue(this.selectedFrac$.value.jobName);
          this.longitudeFormControl.setValue(this.selectedFrac$.value.lngLat[0]);
          this.latitudeFormControl.setValue(this.selectedFrac$.value.lngLat[1]);
          this.radiusInMilesFormControl.setValue(this.selectedFrac$.value.radius);
          this.directionsFormControl.setValue(this.selectedFrac$.value?.directions);
          this.initMap();
        }
      });
  }

  private onRadiusChanges() {
    this.radiusInMilesFormControl.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.geofence?.setRadius(this.radiusInMilesFormControl.value * metersInAMile);
    });

    this.latitudeFormControl.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.fracMarker?.setPosition(
        new google.maps.LatLng(this.latitudeFormControl.value, this.longitudeFormControl.value),
      );
      this.map?.setCenter(this.fracMarker?.getPosition());
    });

    this.longitudeFormControl.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      this.fracMarker?.setPosition(
        new google.maps.LatLng(this.latitudeFormControl.value, this.longitudeFormControl.value),
      );
      this.map?.setCenter(this.fracMarker?.getPosition());
    });
  }

  public updateBoxPickupSite() {
    if (this.createBoxPickUpSiteFormGroup.invalid) {
      return;
    }

    const data: CreateBoxPickupSiteRequest = {
      id: this.data.id,
      siteId: this.data.siteId,
      siteName: this.siteNameFormControl.value,
      siteLong: this.longitudeFormControl.value,
      siteLat: this.latitudeFormControl.value,
      siteRadius: parseInt((this.radiusInMilesFormControl.value * metersInAMile).toFixed(0), 10),
      siteDirections: this.directionsFormControl.value,
      outOfRouteMiles: this.outOfRouteMilesFormControl.value,
      outOfRouteRateCents: this.costPerMileFormControl.value,
      hasActiveOrders: this.data.hasActiveOrders,
    };

    this.fracService.updateBoxPickupSite$(data).subscribe(
      () => {
        this.snackBar.open(`Successfully updated box pickup site`, null, {
          duration: 2500,
        });
        this.dialogRef.close(true);
      },
      () => {
        this.snackBar.open('Something went wrong', null, {
          duration: 2500,
          panelClass: ['snackbar-error'],
        });
      },
    );
  }

  public submitForm() {
    if (this.createBoxPickUpSiteFormGroup.invalid) {
      return;
    }

    const data: CreateBoxPickupSiteRequest = {
      siteName: this.siteNameFormControl.value,
      siteLong: this.longitudeFormControl.value,
      siteLat: this.latitudeFormControl.value,
      siteRadius: parseInt((this.radiusInMilesFormControl.value * metersInAMile).toFixed(0), 10),
      siteDirections: this.directionsFormControl.value,
      outOfRouteMiles: this.outOfRouteMilesFormControl.value,
      outOfRouteRateCents: this.costPerMileFormControl.value,
    };

    this.fracService.createBoxPickupSite$(data).subscribe(
      () => {
        this.snackBar.open(`Successfully created box pickup site`, null, {
          duration: 2500,
        });
        this.dialogRef.close(true);
      },
      () => {
        this.snackBar.open('Something went wrong', null, {
          duration: 2500,
          panelClass: ['snackbar-error'],
        });
      },
    );
  }

  public cancelAction() {
    this.dialogRef.close(false);
  }

  private handleFracMarkerMove() {
    const newPosition = this.fracMarker.getPosition();
    this.latitudeFormControl.setValue(newPosition.lat(), { emitEvent: false });
    this.longitudeFormControl.setValue(newPosition.lng(), { emitEvent: false });
  }

  private handleGeofenceRadiusChange() {
    const newRadius = this.geofence.getRadius() / metersInAMile;
    if (newRadius <= 10) {
      this.radiusInMilesFormControl.setValue(newRadius, { emitEvent: false });
    } else {
      this.geofence.setRadius(10 * metersInAMile);
    }
  }

  private _filterFrac(value) {
    const filterValue = value.toLowerCase();
    return this.fracs.filter((option) => option.jobName.toLowerCase().indexOf(filterValue) > -1);
  }
}
